import React, { Fragment } from 'react';
import * as Yup from 'yup';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CashManagementCrumbs } from 'front-office/constants/cash-management-crumbs';
import { Card, CardActions, CardContent, Grid, Typography, useEventCallback } from '@mui/material';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import { DateTime } from 'luxon';
import { PmiButton } from 'shell/components/shared/button';
import { FormikErrors, useFormik } from 'formik';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { useNavigate } from 'react-router-dom';
import ServiceList from 'front-office/components/cash-management/shared/service-list';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { Service } from 'shell/models/service';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { HttpResponse } from 'shell/models/http-response';

export interface CheckServiceForm {
  service?: string;
  serviceDesc?: string;
  operator?: string;
  extractionDateFrom?: DateTime | null;
  extractionDateTo?: DateTime | null;
  closeCash?: boolean;
  summary?: string;
  allServices?: Service[];
}

const CashManagementFortnightly = (): React.ReactElement => {
  const printSummary = useHttpClient<HttpResponse<ResponseCertificateGeneric>>(
    `${__CERTIFICATION_API_URL__}/certificazione/controllo-cassa/stampa-cie`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  const { userProfile } = useUserProfile();

  const navigate = useNavigate();

  const codeControlloCassa = userProfile.datiLogin?.profile?.feature?.controlloDiCassa || '';
  const nomeServizioCollegato = userProfile.datiLogin?.profile?.servizio?.descrizioneBreve || '';
  const codiceServizioCollegato =
    userProfile.datiLogin?.profile?.servizio?.key?.codiceServizio || '';

  const defaultService = codeControlloCassa === '1' ? nomeServizioCollegato : '';

  const defaultServiceCode = codeControlloCassa === '1' ? codiceServizioCollegato : '';

  const onSubmit = (form: CheckServiceForm) => {
    (
      printSummary({
        data: {
          codiceServizio: form.service === 'TUTTI' ? 'TUTTI' : form.service,
          descrizioneBreveServizio: form.service === 'TUTTI' ? null : form.serviceDesc,
          tuttiIservizi:
            form.service === 'TUTTI'
              ? (form.allServices || []).map((service) => service.key.codiceServizio)
              : null,
          dataEstrazioneDa: form.extractionDateFrom?.toFormat('yyyy-MM-dd'),
          dataEstrazioneA: form.extractionDateTo?.toFormat('yyyy-MM-dd'),
        },
      }) as ResponsePromise<HttpResponse<ResponseCertificateGeneric>>
    ).then((response) => {
      const base64 = response?.data?.payload?.exportCieRilasciate || '';
      const blob = base64ToBlob(base64);
      downloadBlob(blob, `export-cie-rilasciate-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
    });
  };
  const fortnightlyForm = useFormik({
    initialValues: {
      service: defaultServiceCode || '',
      serviceDesc: defaultService || '',
      operator: '',
      extractionDateFrom: null,
      extractionDateTo: null,
      closeCash: false,
      summary: '',
      allServices: [],
    },
    onSubmit,
    validationSchema: Yup.object().shape({
      service: Yup.string().required('Il campo è obbligatorio'),
      extractionDateFrom: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      extractionDateTo: Yup.date()
        .typeError('Inserire una data valida')
        .nullable()
        .required('Il campo è obbligatorio'),
    }),
  });

  const onChangeServiceList = useEventCallback((service: Service | Service[]) => {
    if (!Array.isArray(service)) {
      fortnightlyForm.setFieldValue('service', service.key.codiceServizio);
      fortnightlyForm.setFieldValue('serviceDesc', service.descrizioneBreve);
    } else {
      fortnightlyForm.setFieldValue('service', 'TUTTI');
      fortnightlyForm.setFieldValue('allServices', service);
    }
  });

  return (
    <Fragment>
      <Breadcrumbs
        items={[...CashManagementCrumbs, { name: 'Stampa quindicinale CIE rilasciate' }]}
      />
      <Card raised className="pmi-my-3">
        <form onSubmit={fortnightlyForm.handleSubmit}>
          <CardContent style={{ padding: '24px' }}>
            <Typography gutterBottom variant="h5" component="h2">
              Stampa quindicinale CIE rilasciate
            </Typography>
            <div style={{ padding: '24px 0 0 0' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  {codeControlloCassa === '1' ? (
                    <PmiTextField
                      id="service"
                      value={fortnightlyForm.values.serviceDesc}
                      disabled
                      label="Servizio"
                      fullWidth
                    />
                  ) : (
                    <ServiceList
                      auth={codeControlloCassa === '2'}
                      value={fortnightlyForm.values.service}
                      onChange={onChangeServiceList}
                      allServices
                      error={!!fortnightlyForm.touched.service && !!fortnightlyForm.errors.service}
                      errorMessage={fortnightlyForm.errors.service as FormikErrors<string>}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={5}></Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data inizio validità"
                    aria-label="Data inizio validità"
                    value={fortnightlyForm.values.extractionDateFrom || null}
                    onChange={(date) => {
                      fortnightlyForm.setFieldValue('extractionDateFrom', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="extractionDateFrom"
                        fullWidth
                        error={
                          !!fortnightlyForm.touched.extractionDateFrom &&
                          !!fortnightlyForm.errors.extractionDateFrom
                        }
                        helperText={
                          fortnightlyForm.errors.extractionDateFrom as FormikErrors<string>
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <DatePicker
                    label="Data fine validità"
                    aria-label="Data fine validità"
                    value={fortnightlyForm.values.extractionDateTo || null}
                    onChange={(date) => {
                      fortnightlyForm.setFieldValue('extractionDateTo', date);
                    }}
                    renderInput={(params) => (
                      <PmiTextField
                        {...params}
                        id="extractionDateTo"
                        fullWidth
                        error={
                          !!fortnightlyForm.touched.extractionDateTo &&
                          !!fortnightlyForm.errors.extractionDateTo
                        }
                        helperText={fortnightlyForm.errors.extractionDateTo as FormikErrors<string>}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          </CardContent>
          <CardActions style={{ padding: '24px 24px 16px 24px' }}>
            <PmiButton id="submitForm" variant="contained" color="primary" type="submit">
              Conferma
            </PmiButton>
            <PmiButton
              id="goBack"
              variant="outlined"
              color="cdmBrand"
              onClick={() => {
                navigate('/sportello/gestione-cassa');
              }}
            >
              Annulla
            </PmiButton>
          </CardActions>
        </form>
      </Card>
    </Fragment>
  );
};

CashManagementFortnightly.displayName = 'Gestione cassa navigation menu';

export default CashManagementFortnightly;
